import type { ComponentType } from 'react';

/**
 * Cross-feature presentation primitives injected into `CustomWorkflowInlineInput`
 * (Springboard dependency injection) to keep this slice free of cross-feature OUT to
 * `@/features/chats`. The consumer (e.g. chats `ImageGenerationOptions`)
 * passes its `ChatPopupTextInput` / `ChatPopupNumberInput` / `ChatOptionDropdown`
 * primitives in.
 */
export interface CustomWorkflowInputComponents {
  TextInput: ComponentType<{
    label: string;
    defaultText: string;
    onChange: (value: string) => void;
  }>;
  NumberInput: ComponentType<{
    label: string;
    defaultValue?: number;
    min?: number;
    max?: number;
    isInteger?: boolean;
    onChange: (value: number) => void;
  }>;
  OptionDropdown: ComponentType<{
    label: string;
    description: string;
    initialValue: string;
    value: string;
    onChange: (value: string) => void;
    options: { label: string; value: string }[];
  }>;
}

export interface CustomWorkflowInlineInputProps {
  customWorkflowId: string;
  data?: Record<string, string | number>;
  onChange?: (data: Record<string, string | number>) => void;
  /**
   * Concrete chat-input component implementations injected by the consumer.
   * Decouples this slice from `@/features/chats`.
   */
  components: CustomWorkflowInputComponents;
}

export enum InputTypes {
  TEXT = 'text',
  NUMBER = 'number',
  INTEGER = 'integer',
  ENUM = 'enum',
  ARRAY = 'array',
}

export interface PipelineInput {
  name: string;
  title: string;
  dataSchema: {
    type: string;
    enum?: string[];
    [key: string]: unknown;
  };
  config: {
    required?: boolean;
    minimum?: number;
    maximum?: number;
    enumOptions?: string[];
    [key: string]: unknown;
  };
}

export interface InputField {
  name: string;
  label: string;
  type: InputTypes;
  enumOptions?: string[];
  minimum?: number;
  maximum?: number;
}

export interface FieldProps {
  field: InputField;
  value?: string | number;
  onChange: (name: string, value: string | number) => void;
}
